import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	CatalogOption,
	OrderStatusOption,
	PurchaseOrder,
	PurchaseOrderDetail,
	RollBlankOption,
	UpsertPurchaseOrderDetailParams,
	UpsertPurchaseOrderMasterParams,
} from "../models/PurchaseOrders";

// Reads and writes both go straight to the ROLLS procedures.

export const getPurchaseOrders = async ({
	search = null,
	onlyActives = false,
	includeDeleted = false,
	signal,
}: {
	search?: string | null;
	onlyActives?: boolean;
	includeDeleted?: boolean;
	signal?: AbortSignal;
} = {}): Promise<PurchaseOrder[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "id", value: null },
		{ name: "search", value: search },
		{ name: "onlyActives", value: onlyActives },
		{ name: "includeItems", value: false },
		{ name: "includeDeleted", value: includeDeleted },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[GetPurchaseOrders]",
		parameters,
		signal,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching purchase orders");
	}
	return get(resp, "data.tables[0].rows", []) as PurchaseOrder[];
};

export const getPurchaseOrderDetails = async ({
	purchaseOrderId,
	signal,
}: {
	purchaseOrderId: number;
	signal?: AbortSignal;
}): Promise<PurchaseOrderDetail[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "id", value: null },
		{ name: "onlyActives", value: false },
		{ name: "purchaseOrderId", value: purchaseOrderId },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[GetPurchaseOrderDetails]",
		parameters,
		signal,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching purchase order items");
	}
	return get(resp, "data.tables[0].rows", []) as PurchaseOrderDetail[];
};

// ----------------------------------------------------------------------------
// Writes (direct to new ROLLS DB)
// ----------------------------------------------------------------------------

export const upsertPurchaseOrderMaster = async (
	params: UpsertPurchaseOrderMasterParams,
): Promise<number> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: params.Id ?? null },
		{ name: "Active", value: params.Active ?? true },
		{ name: "Number", value: params.Number },
		{ name: "RequisitionNumber", value: params.RequisitionNumber ?? null },
		{ name: "SupplierId", value: params.SupplierId ?? null },
		{ name: "CostCenterId", value: params.CostCenterId ?? null },
		{ name: "DateOrdered", value: params.DateOrdered ?? null },
		{ name: "OrderStatusCode", value: params.OrderStatusCode ?? null },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[UpsertPurchaseOrderMaster]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error saving purchase order");
	}
	return get(resp, "data.tables[0].rows[0].Id", 0) as number;
};

export const upsertPurchaseOrderDetail = async (
	params: UpsertPurchaseOrderDetailParams,
): Promise<number> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "Id", value: params.Id ?? null },
		{ name: "Active", value: params.Active ?? true },
		{ name: "PurchaseOrderId", value: params.PurchaseOrderId },
		{ name: "RollBlankDiameter", value: params.RollBlankDiameter ?? null },
		{ name: "QuantityOrdered", value: params.QuantityOrdered ?? null },
		{ name: "RollCost", value: params.RollCost ?? null },
		{ name: "SupplierId", value: params.SupplierId ?? null },
		{ name: "CostCenterId", value: params.CostCenterId ?? null },
		{ name: "RollBlankId", value: params.RollBlankId ?? null },
	];

	const resp = await apiService.callV2(
		"[ROLLS].[UpsertPurchaseOrderDetail]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error saving purchase order item");
	}
	return get(resp, "data.tables[0].rows[0].Id", 0) as number;
};

// Both remove SPs keep the legacy contract: instead of throwing, a blocked
// delete returns a result row with a [Message] code. Read it — checking only
// resp.ok makes those outcomes look like clean successes.

// Returns a warning to display when the PO could not be physically removed
// (it has received rolls) and was soft-deleted instead.
export const removePurchaseOrder = async (
	id: number,
): Promise<string | null> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[ROLLS].[RemovePurchaseOrder]", [
		{ name: "Id", value: id },
	]);
	if (!resp.ok) {
		throw new Error(resp.message || "Error removing purchase order");
	}
	const message = get(resp, "data.tables[0].rows[0].Message", null) as
		| string
		| null;
	return message === "PURCHASE_ORDER_MARKED_AS_DELETED"
		? "The purchase order has received rolls, so it was marked as deleted instead of removed."
		: null;
};

export const removePurchaseOrderDetail = async (id: number): Promise<void> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[ROLLS].[RemovePurchaseOrderDetail]", [
		{ name: "Id", value: id },
	]);
	if (!resp.ok) {
		throw new Error(resp.message || "Error removing purchase order item");
	}
	const message = get(resp, "data.tables[0].rows[0].Message", null) as
		| string
		| null;
	if (message === "DB_ERROR_CAN_NOT_REMOVE_ITEM_USED") {
		throw new Error(
			"The item cannot be removed because it already has rolls received.",
		);
	}
};

// ----------------------------------------------------------------------------
// Auxiliary catalogs (read directly from the new schema; not proxy-bifurcated
// since catalog data is identical in legacy and new).
// ----------------------------------------------------------------------------

export const getSuppliers = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<CatalogOption[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[MILL].[GetSuppliers]",
		[{ name: "OnlyActives", value: false }],
		signal,
	);
	if (resp.ok) return get(resp, "data.tables[0].rows", []) as CatalogOption[];
	throw new Error(resp.message || "Error fetching suppliers");
};

export const getCostCenters = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<CatalogOption[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[MILL].[GetCostCenters]",
		[{ name: "OnlyActives", value: false }],
		signal,
	);
	if (resp.ok) return get(resp, "data.tables[0].rows", []) as CatalogOption[];
	throw new Error(resp.message || "Error fetching cost centers");
};

export const getOrderStatus = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<OrderStatusOption[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[MILL].[GetOrderStatus]",
		[{ name: "Code", value: null }],
		signal,
	);
	if (resp.ok)
		return get(resp, "data.tables[0].rows", []) as OrderStatusOption[];
	throw new Error(resp.message || "Error fetching order status");
};

export const getRollBlanks = async ({
	signal,
}: {
	signal?: AbortSignal;
} = {}): Promise<RollBlankOption[]> => {
	const apiService = new MESApiService();
	// Legacy parity: the Roll Blank dropdown (CAT.GetRollsBlank) listed active
	// blanks only.
	const resp = await apiService.callV2(
		"[ROLLS].[GetRollBlanks]",
		[{ name: "OnlyActives", value: true }],
		signal,
	);
	if (resp.ok) return get(resp, "data.tables[0].rows", []) as RollBlankOption[];
	throw new Error(resp.message || "Error fetching roll blanks");
};
